"use client"

import Image from "next/image"
import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { getTranslations } from "@/lib/translations"
import type { Locale } from "@/lib/i18n"

type CTAProps = {
  locale: Locale
}

export function CTA({ locale }: CTAProps) {
  const t = getTranslations(locale)
  return (
    <section className="relative overflow-hidden pt-0 pb-0">
      <div className="relative w-full min-h-[400px] sm:min-h-[500px] lg:min-h-[500px] overflow-hidden bg-[#034aa6]" suppressHydrationWarning>
        {/* Mobile: Background Image */}
        <div className="absolute inset-0 lg:hidden" suppressHydrationWarning>
          <Image
            src="/CTA.jpg"
            alt="Birlikte Çalışalım"
            fill
            className="object-cover"
            quality={75}
            priority
            sizes="(max-width: 640px) 412px, 640px"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-900/90" suppressHydrationWarning />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(3,74,166,0.2),transparent_70%)]" suppressHydrationWarning />
        </div>

        {/* Desktop: Sağ Taraf Arka Plan Görseli */}
        <div className="hidden lg:block absolute inset-0" suppressHydrationWarning>
          <div className="absolute left-1/2 top-0 bottom-0 right-0" suppressHydrationWarning>
            <Image
              src="/CTA.jpg"
              alt="Birlikte Çalışalım"
              fill
              className="object-cover"
              quality={80}
              sizes="50vw"
            />
          </div>
        </div>

        {/* Desktop: Grid Layout */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full" suppressHydrationWarning>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center min-h-[400px] sm:min-h-[500px] lg:min-h-[500px] py-8 sm:py-12 lg:py-16" suppressHydrationWarning>
            {/* Sol Taraf - Yazılar */}
            <div className="text-center lg:text-left text-white flex flex-col justify-center relative z-10 min-h-[400px] sm:min-h-[500px] lg:min-h-0" suppressHydrationWarning>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight mb-4 sm:mb-6 leading-tight">
                <span>{t.cta.title}</span>
                <span className="text-blue-300">{t.cta.titleHighlight}</span>
              </h2>

              <p className="text-base sm:text-lg text-slate-200 mb-6 sm:mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {t.cta.description}
              </p>

              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4" suppressHydrationWarning>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white text-slate-900 hover:bg-slate-100 px-6 sm:px-8 py-4 sm:py-5 h-auto rounded-lg font-semibold text-sm sm:text-base shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300"
                >
                  <Link href={`/${locale}/teklif-al`} className="flex items-center">
                    {t.cta.button}
                    <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Link>
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/20" suppressHydrationWarning>
                <div className="grid grid-cols-3 gap-4 sm:gap-6" suppressHydrationWarning>
                  <div suppressHydrationWarning>
                    <div className="text-xl sm:text-2xl font-bold mb-1" suppressHydrationWarning>50+</div>
                    <div className="text-xs text-slate-300" suppressHydrationWarning>{t.cta.stats.projects}</div>
                  </div>
                  <div suppressHydrationWarning>
                    <div className="text-xl sm:text-2xl font-bold mb-1" suppressHydrationWarning>%100</div>
                    <div className="text-xs text-slate-300" suppressHydrationWarning>{t.cta.stats.satisfaction}</div>
                  </div>
                  <div suppressHydrationWarning>
                    <div className="text-xl sm:text-2xl font-bold mb-1" suppressHydrationWarning>5+</div>
                    <div className="text-xs text-slate-300" suppressHydrationWarning>{t.cta.stats.experience}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sağ Taraf - Boş (Görsel arka planda) */}
            <div className="hidden lg:block" suppressHydrationWarning></div>
          </div>
        </div>
      </div>
    </section>
  )
}
